import type { ScaleTime } from 'd3-scale';

import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import { TimeseriesDatapoint } from '../../../../core/types/timeseries.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { YAxisScales } from '../../../types/state.js';
import { WeakMapDatapoints } from '../../../types/timeseries.js';
import { getDatapointTime } from '../../../utils/timeseries.js';
import { getYScaleByUnit } from '../../../utils/yAxis.js';
import { getValueWithBoundaries } from '../shared/utils/clamp-value.js';

/**
 * Return boolean related if data-point is gap
 *
 * @param datapoint - The data points
 * @returns Wether the accessor is defined
 */
export function definedAccessor(
  datapoint: TimeseriesDatapoint,
  datapointsMap: WeakMapDatapoints,
): boolean {
  return !safeGet(datapointsMap, datapoint).isGap;
}

/**
 * Get x-accessor base on xScale
 *
 * @param xScale - The x axis scale
 * @param clampedScale - the clamped scale
 * @returns The line chart x-accessor
 */
export function xAxisAccessor(
  xScale: ScaleTime<number, number>,
  datapointsMap: WeakMapDatapoints,
  clampedScale: ScaleTime<number, number>,
) {
  return (dataPoint: TimeseriesDatapoint) => {
    const { start, end, center } = safeGet(datapointsMap, dataPoint);
    const timestamp = getDatapointTime(start, end, center);

    return getValueWithBoundaries(timestamp, xScale, clampedScale);
  };
}

/**
 * Get y-accessor base on yScale
 *
 * @param unit - The series unit
 * @param yScales - The y axis scale
 * @param datapointsMap -
 * @returns The line chart y-accessor
 */
export function yAxisAccessor(
  yScales: YAxisScales,
  datapointsMap: WeakMapDatapoints,
  unit?: ChartUnit,
) {
  return (point: TimeseriesDatapoint) => {
    const { value } = safeGet(datapointsMap, point);
    const scale = getYScaleByUnit(unit, yScales);

    return scale(value ?? 0);
  };
}
